// Blank + pre-filled template downloads for the Phase 2 Life modules.
//
// All 5 generators (Budget, Sadaqah, Waqf, Estate, EOL) share the
// Amanah brand theme defined in `./amanahDocxTheme.js` — gold-bordered
// tables, emerald headings, cover page with kicker + ornament, Islamic
// principle callouts and Qur'an/Hadith blockquotes for context.
//
// Two output flavours where it makes sense:
//   - .csv   for tabular data (Budget, Sadaqah)
//   - .docx  for narrative / form-style data (Waqf, EOL, Estate, plus a
//            richly-formatted flavour for Budget & Sadaqah)
//
// CSV is generated with native JS — no dep. Word docs use the existing
// `docx` library (already in the bundle for the Wasiyyah writer) and are
// dynamically imported so the chunk is fetched only when the user clicks
// Download.

// -----------------------------------------------------------------
// Small generic helpers
// -----------------------------------------------------------------

const stamp = () => new Date().toISOString().slice(0, 10);

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// RFC-4180 friendly CSV escape (handles commas, quotes, newlines)
const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const csvRow = (cells) => cells.map(csvEscape).join(',');

// Lazy docx loader. The library is ~250KB gz and lives in its own chunk.
const loadDocx = () => import('docx');

// Amanah brand theme — shared cover / headings / tables / callouts / quotes.
import {
  buildAmanahSection, buildAmanahCover,
  themedHeading, themedBody, themedBullet, themedTable, themedCallout,
  themedQuote, themedSignatureBlock, themedOrnament,
} from './amanahDocxTheme';

// CSV → Blob helper.
const csvToBlobOrDownload = (rows, filename, asBlob) => {
  const csv = rows.map(csvRow).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  if (asBlob) return blob;
  downloadBlob(blob, filename);
};
// Docx Document → Blob helper.
const docxToBlobOrDownload = async (Packer, doc, filename, asBlob) => {
  const blob = await Packer.toBlob(doc);
  if (asBlob) return blob;
  downloadBlob(blob, filename);
  return blob;
};

// -----------------------------------------------------------------
// 1. BUDGET — Family Financial Planning
// -----------------------------------------------------------------

export const downloadBudgetCsv = (state = null, { asBlob = false } = {}) => {
  const incomes = state?.incomes || [];
  const expenses = state?.expenses || [];
  const pots = state?.pots || {};
  const rows = [
    ['# Amanah by Zakat.International — Family Financial Planning'],
    [`# Generated: ${stamp()}  ·  Currency: ${state?.currency || 'USD'}`],
    [],
    ['Section', 'Label', 'Amount'],
    ...incomes.map((r) => ['INCOME', r.label || '', r.amount || 0]),
    ['INCOME', '(blank rows for handwriting)', 0],
    ['INCOME', '', ''],
    ['INCOME', '', ''],
    [],
    ...expenses.map((r) => ['EXPENSE', r.label || '', r.amount || 0]),
    ['EXPENSE', '(blank rows for handwriting)', 0],
    ['EXPENSE', '', ''],
    ['EXPENSE', '', ''],
    [],
    ['POT', 'Zakat reserve', pots.zakat || 0],
    ['POT', 'Hajj fund', pots.hajj || 0],
    ['POT', 'Sadaqah jar', pots.sadaqah || 0],
    ['POT', 'Emergency fund', pots.emergency || 0],
    [],
    ['# Surplus = SUM(income) − SUM(expenses).  Compare to total pot target.'],
  ];
  return csvToBlobOrDownload(rows, `amanah-budget-${state ? 'mine' : 'blank'}-${stamp()}.csv`, asBlob);
};

export const downloadBudgetDocx = async (state = null, { asBlob = false } = {}) => {
  const docx = await loadDocx();
  const { Document, Packer } = docx;
  const isBlank = !state;
  const currency = state?.currency || 'USD';

  const incomes = state?.incomes || Array.from({ length: 6 }, () => ({ label: '', amount: '' }));
  const expenses = state?.expenses || Array.from({ length: 8 }, () => ({ label: '', amount: '' }));
  const pots = state?.pots || {};

  const HW = '\u2005\u2005\u2005\u2005\u2005\u2005\u2005\u2005\u2005\u2005\u2005\u2005'; // 12 four-per-em spaces
  const withHW = (v) => v || HW;

  const sumIncome = incomes.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const sumExpense = expenses.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const surplus = sumIncome - sumExpense;
  const potTotal = Number(pots.zakat || 0) + Number(pots.hajj || 0) + Number(pots.sadaqah || 0) + Number(pots.emergency || 0);

  const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0);

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'Family Financial Planning',
    contextTag: 'Monthly Budget',
    children: [
      ...buildAmanahCover(docx, {
        kicker: 'Amanah Life Planning Kit · 01',
        title: 'Family Financial Planning',
        subtitle: 'Halal income, disciplined expenses, sincere savings.',
      }),

      themedQuote(docx, {
        arabic: 'وَأَنفِقُوا مِمَّا رَزَقْنَاكُمْ',
        translation: 'And spend of what We have provided for you…',
        source: "Qur'an 63:10",
      }),

      themedCallout(docx, {
        kicker: 'Why this matters',
        body: 'A monthly budget is the first hisab (accounting) a believer keeps. Balance halal income against expenses, then reserve for four Islamic savings pots — Zakat, Hajj, Sadaqah and Emergency — so obligation, worship, charity and resilience each get a place before the surplus disappears.',
        tone: 'emerald',
      }),

      themedHeading(docx, `1 · Monthly income  (${currency})`),
      themedTable(docx, {
        columns: [{ label: 'Source', width: 70 }, { label: 'Amount', width: 30 }],
        rows: [
          ...incomes.map((r) => [withHW(r.label), r.amount ? fmt(r.amount) : HW]),
          ['TOTAL INCOME', isBlank ? HW : fmt(sumIncome)],
        ],
        tone: 'emerald',
      }),

      themedHeading(docx, `2 · Monthly expenses  (${currency})`),
      themedTable(docx, {
        columns: [{ label: 'Expense', width: 70 }, { label: 'Amount', width: 30 }],
        rows: [
          ...expenses.map((r) => [withHW(r.label), r.amount ? fmt(r.amount) : HW]),
          ['TOTAL EXPENSES', isBlank ? HW : fmt(sumExpense)],
        ],
        tone: 'emerald',
      }),

      themedHeading(docx, `3 · Islamic savings pots  (monthly target, ${currency})`),
      themedTable(docx, {
        columns: [{ label: 'Pot', width: 60 }, { label: 'Monthly target', width: 20 }, { label: 'Purpose', width: 20 }],
        rows: [
          ['Zakat reserve',   pots.zakat    ? fmt(pots.zakat)    : HW, '2.5% obligation'],
          ['Hajj fund',       pots.hajj     ? fmt(pots.hajj)     : HW, 'Once-in-life'],
          ['Sadaqah jar',     pots.sadaqah  ? fmt(pots.sadaqah)  : HW, 'Voluntary'],
          ['Emergency fund',  pots.emergency ? fmt(pots.emergency) : HW, '3–6 mo. cover'],
          ['TOTAL POT TARGET', isBlank ? HW : fmt(potTotal), ''],
        ],
        tone: 'gold',
      }),

      themedHeading(docx, '4 · Surplus check'),
      themedBody(docx, `Surplus  =  Total income  −  Total expenses  =  ${isBlank ? '_______' : fmt(surplus) + ' ' + currency}`),
      themedBody(docx, `Compare to Total pot target (${isBlank ? '_______' : fmt(potTotal) + ' ' + currency}). If the surplus is smaller, either trim an expense, reduce a pot target, or split the shortfall proportionally across pots.`),

      themedCallout(docx, {
        kicker: 'Islamic finance principles',
        body: '• Earn only from halal sources (no riba, no gharar, no haram trade).  • Pay Zakat annually on the lunar anniversary of your nisab date.  • Debt takes priority over voluntary charity.  • Reserve emergency funds before speculation.  • The niyyah (intention) counts as much as the amount.',
        tone: 'gold',
      }),

      themedOrnament(docx),
      themedBody(docx, `Snapshot generated ${stamp()}.`, { run: { italics: true, color: '9E9E9E', size: 16 } }),
    ],
  })] });
  return docxToBlobOrDownload(Packer, doc, `amanah-budget-${isBlank ? 'blank' : 'mine'}-${stamp()}.docx`, asBlob);
};

// -----------------------------------------------------------------
// 2. SADAQAH — Charity log
// -----------------------------------------------------------------

const SADAQAH_HEADERS = ['Date', 'Amount', 'Type', 'Recipient', 'Intent (niyyah)', 'Anonymous?'];

export const downloadSadaqahCsv = (state = null, { asBlob = false } = {}) => {
  const entries = state?.entries || [];
  const filled = entries.map((e) => [e.date || '', e.amount || '', e.type || '', e.anonymous ? '(anonymous)' : (e.recipient || ''), e.intent || '', e.anonymous ? 'Yes' : 'No']);
  const blanks = Array.from({ length: 20 }, () => ['', '', '', '', '', '']);
  const rows = [
    ['# Amanah by Zakat.International — Sadaqah Tracker'],
    [`# Generated: ${stamp()}  ·  Currency: ${state?.currency || 'USD'}`],
    [],
    SADAQAH_HEADERS,
    ...filled,
    ...blanks,
  ];
  return csvToBlobOrDownload(rows, `amanah-sadaqah-${state ? 'mine' : 'blank'}-${stamp()}.csv`, asBlob);
};

export const downloadSadaqahDocx = async (state = null, { asBlob = false } = {}) => {
  const docx = await loadDocx();
  const { Document, Packer } = docx;
  const isBlank = !state;
  const currency = state?.currency || 'USD';
  const entries = state?.entries || [];

  const HW = '\u2005\u2005\u2005\u2005\u2005\u2005\u2005\u2005';

  const rows = entries.map((e) => [
    e.date || '', String(e.amount || ''), e.type || '',
    e.anonymous ? '(anonymous)' : (e.recipient || ''),
    e.intent || '',
  ]);
  const blankRowCount = Math.max(20 - rows.length, 10);
  for (let i = 0; i < blankRowCount; i++) rows.push([HW, HW, HW, HW, HW]);

  const total = entries.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'Sadaqah Tracker',
    contextTag: 'Lifetime Giving Log',
    children: [
      ...buildAmanahCover(docx, {
        kicker: 'Amanah Life Planning Kit · 02',
        title: 'Sadaqah Tracker',
        subtitle: 'A private ledger of sincerity — every act of charity, dated & intent-tagged.',
      }),

      themedQuote(docx, {
        arabic: 'مَا نَقَصَتْ صَدَقَةٌ مِنْ مَالٍ',
        translation: 'Sadaqah does not decrease wealth.',
        source: 'Sahih Muslim 2588',
      }),

      themedCallout(docx, {
        kicker: 'Why this matters',
        body: 'Logging each act of charity — anonymous or named — turns generosity into a habit and a du\u2018a-generating record. It also helps you separate obligatory Zakat from voluntary sadaqah at year-end, and identify recurring causes for a future sadaqah jariyah.',
        tone: 'emerald',
      }),

      themedHeading(docx, `Log  (${currency})`),
      themedTable(docx, {
        columns: [
          { label: 'Date', width: 15 },
          { label: 'Amount', width: 12 },
          { label: 'Type', width: 18 },
          { label: 'Recipient', width: 25 },
          { label: 'Niyyah (intent)', width: 30 },
        ],
        rows,
        tone: 'emerald',
      }),

      isBlank ? null : themedBody(docx, `Total logged this period: ${new Intl.NumberFormat('en-US').format(total)} ${currency}`, { run: { bold: true, color: '0D2D22' } }),

      themedHeading(docx, 'Types of Sadaqah — a quick reference'),
      themedCallout(docx, {
        kicker: 'Regular · Jariyah · Sirri · Alaniyah',
        body: '• Sadaqah — any voluntary act of charity (time, money, kind words).  • Sadaqah jariyah — a gift that keeps giving (a well, an endowment, teaching).  • Sirri (secret) — hidden charity, most beloved to Allah.  • Alaniyah (open) — public charity that inspires others.  The Prophet ﷺ said even a smile is charity.',
        tone: 'gold',
      }),

      themedHeading(docx, 'Zakat vs Sadaqah — not the same'),
      themedBody(docx, 'Zakat is a 2.5% obligation on wealth held for a lunar year, payable to eight specific categories in Qur\u2019an 9:60. Sadaqah is voluntary and unrestricted. Use the Amanah Zakat calculator for the obligation and this log for the voluntary track.', { run: { italics: true, color: '5C5C5C' } }),

      themedOrnament(docx),
      themedBody(docx, isBlank ? 'Blank log. Print or open in Word and fill each row.' : `Snapshot generated ${stamp()}.`, { run: { italics: true, color: '9E9E9E', size: 16 } }),
    ].filter(Boolean),
  })] });
  return docxToBlobOrDownload(Packer, doc, `amanah-sadaqah-${isBlank ? 'blank' : 'mine'}-${stamp()}.docx`, asBlob);
};

// -----------------------------------------------------------------
// 3. WAQF — deed-of-waqf draft (Word)
// -----------------------------------------------------------------

const WAQF_CAUSES = {
  water:       'Water — wells, filtration, drought relief',
  education:   'Education — madrasah, scholarships, teacher salaries',
  masjid:      'Masjid — construction, maintenance, imam stipend',
  healthcare:  'Healthcare — clinic, hospital, medicine',
  orphan:      'Orphan care — housing, tuition, guardianship',
  environment: 'Environment — tree planting, reforestation',
};

export const downloadWaqfDocx = async (state = null, { asBlob = false } = {}) => {
  const docx = await loadDocx();
  const { Document, Packer, Paragraph, TextRun, UnderlineType } = docx;
  const isBlank = !state;

  const blank = (w = 40) => new TextRun({
    text: '\u00A0'.repeat(w),
    underline: { type: UnderlineType.SINGLE, color: '888888' },
    size: 22,
  });
  const line = (label, value) => new Paragraph({
    spacing: { after: 140 },
    children: [
      new TextRun({ text: `${label}: `, bold: true, size: 22, color: '0D2D22', font: 'Segoe UI' }),
      value ? new TextRun({ text: String(value), size: 22, font: 'Segoe UI' }) : blank(),
    ],
  });

  const causeLabel = (() => {
    if (!state) return null;
    if (state.cause === 'custom') return state.customCause || '(custom — to be named)';
    return WAQF_CAUSES[state.cause] || state.cause;
  })();

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'Deed of Waqf — Draft',
    contextTag: 'Islamic Endowment',
    children: [
      ...buildAmanahCover(docx, {
        kicker: 'Amanah Life Planning Kit · 03',
        title: 'Deed of Waqf',
        subtitle: 'A perpetual Islamic endowment — sadaqah jariyah that outlives you.',
      }),

      themedQuote(docx, {
        arabic: 'إِذَا مَاتَ الْإِنْسَانُ انْقَطَعَ عَنْهُ عَمَلُهُ إِلَّا مِنْ ثَلَاثَةٍ: صَدَقَةٍ جَارِيَةٍ...',
        translation: "When a person dies, all their deeds end except three: a sadaqah jariyah (running charity), knowledge that benefits others, and a righteous child who prays for them.",
        source: 'Sahih Muslim 1631',
      }),

      themedCallout(docx, {
        kicker: 'Why a waqf',
        body: "A waqf is one of the three ongoing charities. Once declared, the corpus is locked (mawqūf), the income perpetually benefits the cause, and every soul it helps counts on your scale until the Day of Judgement. Historical waqf endowments funded Al-Azhar, the Ottoman soup-kitchens (imaret), and the Sabil-Kuttabs of Cairo.",
        tone: 'emerald',
      }),

      themedCallout(docx, {
        kicker: 'Five pillars of a valid waqf',
        body: '1. Wāqif — donor with legal capacity and ownership.  2. Mawqūf — the property, halal and identifiable.  3. Mawqūf ‘alayh — a lawful, continuous cause.  4. Sighah — a clear declaration (this deed).  5. Perpetuity — corpus preserved; only the income is spent.',
        tone: 'gold',
      }),

      themedHeading(docx, "1 · Donor (Wāqif)"),
      line('Full legal name', state?.donorName),
      line('National ID / passport no.', state?.donorId),
      line('Address', state?.donorAddress),

      themedHeading(docx, '2 · Cause / beneficiary class'),
      line('Cause', causeLabel),
      line('Specific beneficiary / institution', state?.beneficiary),
      line('Region of benefit', state?.region),

      themedHeading(docx, '3 · Property / amount dedicated (mawqūf)'),
      line('Description of property or amount', state?.amount ? `${state.amount} ${state.currency || 'USD'}` : null),
      line('Timing',
        state?.timing === 'lifetime' ? 'LIFETIME — effective immediately and irrevocable'
        : state?.timing === 'testamentary' ? 'TESTAMENTARY — effective at my death (counts against 1/3 wasiyyah cap)'
        : null),
      line('Duration',
        state?.duration === 'perpetual' ? "PERPETUAL (mu'abbad)"
        : state?.duration === 'limited' ? "TIME-LIMITED (mu'aqqat)"
        : null),

      themedHeading(docx, '4 · Mutawallī (Trustee)'),
      line('Trustee name', state?.trustee),
      line('Trustee contact', state?.trusteeContact),
      line('Alternate trustee', state?.alternateTrustee),
      themedBody(docx, "The mutawallī shall invest the corpus in halal instruments only and disburse the income to the beneficiary cause above. The corpus (mawqūf) shall remain intact unless the time-limited option applies.", { run: { italics: true, color: '5C5C5C', size: 20 } }),

      themedHeading(docx, '5 · Additional terms'),
      themedBody(docx, state?.notes || '[Free-text terms — investment policy, succession of mutawallī, dispute-resolution scholar, permitted admin ratio, etc.]'),

      themedHeading(docx, '6 · Witnesses & registration'),
      themedBody(docx, "This deed must be (a) signed before two adult Muslim witnesses and (b) registered with the competent civil authority in the donor's jurisdiction (Awqāf Ministry, notary public, land registry). It is NOT a legally binding instrument until executed and registered locally."),

      ...themedSignatureBlock(docx, {
        parties: [
          { label: 'Donor (Wāqif) signature' },
          { label: 'Date' },
          { label: 'Witness 1 — Name' },
          { label: 'Witness 1 — Signature' },
          { label: 'Witness 2 — Name' },
          { label: 'Witness 2 — Signature' },
          { label: 'Registrar / Notary' },
        ],
      }),

      themedOrnament(docx),
      themedBody(docx, '⚠ This is a starting draft, not a legal instrument. Have it reviewed by a qualified scholar AND a lawyer or notary in your jurisdiction before signing.', { run: { italics: true, color: '9E9E9E', size: 16 } }),
    ],
  })] });
  return docxToBlobOrDownload(Packer, doc, `amanah-waqf-deed-${isBlank ? 'blank' : 'mine'}-${stamp()}.docx`, asBlob);
};

// -----------------------------------------------------------------
// 4. END-OF-LIFE CHECKLIST (Word)
// -----------------------------------------------------------------

const EOL_SECTIONS = [
  { id: 'shahadah', title: 'Last words & wishes', priority: 'MUST', fields: [
    ['shahadahWish', 'Should I be reminded to say the Shahādah at end of life?'],
    ['preferredCompanion', 'Who should be present, in priority order?'],
    ['forgivenessNote', 'People I forgive / from whom I seek forgiveness'],
  ] },
  { id: 'ghusl', title: 'Ghusl & shrouding', priority: 'MUST', fields: [
    ['ghuslPerson', 'Who should perform the ghusl?'],
    ['kafanMaterial', 'Kafan (shroud) material preference'],
    ['ghuslLocation', 'Where should ghusl take place?'],
  ] },
  { id: 'janazah', title: 'Janāzah & burial', priority: 'MUST', fields: [
    ['janazahMasjid', 'Preferred masjid for janāzah prayer'],
    ['burialLocation', 'Burial location (city / cemetery)'],
    ['burialPlotPurchased', 'Have I purchased a burial plot?'],
    ['burialPlotDetails', 'Plot details / contact'],
    ['travelToHomeland', 'Repatriation to homeland?'],
  ] },
  { id: 'debts', title: 'Debts', priority: 'MUST', fields: [
    ['debtsIOwe', 'Debts I owe (creditor + amount + contact)'],
    ['debtsOwedToMe', 'Debts owed to me'],
    ['unpaidMahr', 'Unpaid mahr to my wife/wives'],
  ] },
  { id: 'executor', title: 'Executor & key contacts', priority: 'SHOULD', fields: [
    ['executorName', 'Executor (waṣī) name'],
    ['executorContact', 'Executor contact (phone + email)'],
    ['alternateExecutor', 'Alternate executor'],
    ['localImam', 'Local imam / scholar to consult'],
    ['lawyerContact', 'Lawyer / notary contact'],
  ] },
  { id: 'documents', title: 'Where to find key documents', priority: 'SHOULD', fields: [
    ['willLocation', 'Where is the signed will stored?'],
    ['passportLocation', 'Passports / ID'],
    ['titleDeeds', 'Property title deeds'],
    ['bankLogins', 'Bank / pension contacts (NOT passwords)'],
    ['digitalAssets', 'Digital assets — crypto / social / email'],
  ] },
  { id: 'family', title: 'For my family', priority: 'NICE', fields: [
    ['finalMessage', 'A final message / advice'],
    ['sadaqahJariyahWish', 'Sadaqah jariyah I want continued in my name'],
    ['duaRequest', 'Specific du\u2018ā I ask of them'],
  ] },
];

export const downloadEolDocx = async (state = null, { asBlob = false } = {}) => {
  const docx = await loadDocx();
  const { Document, Packer, Paragraph, TextRun, UnderlineType } = docx;
  const isBlank = !state;

  const blank = (w = 60) => new TextRun({
    text: '\u00A0'.repeat(w),
    underline: { type: UnderlineType.SINGLE, color: '888888' },
    size: 22,
  });

  const field = (label, value, priority) => {
    const priTag = priority ? new TextRun({
      text: `[${priority}]  `,
      bold: true, size: 16,
      color: priority === 'MUST' ? '8A1010' : priority === 'SHOULD' ? '8A6A18' : '4E4633',
      font: 'Segoe UI',
    }) : null;
    return new Paragraph({
      spacing: { after: 200 },
      children: [
        priTag,
        new TextRun({ text: `${label}: `, bold: true, size: 22, color: '0D2D22', font: 'Segoe UI' }),
        value ? new TextRun({ text: String(value), size: 22, font: 'Segoe UI' }) : blank(),
      ].filter(Boolean),
    });
  };

  const sectionBlocks = [];
  EOL_SECTIONS.forEach((sec, i) => {
    sectionBlocks.push(themedHeading(docx, `${i + 1} · ${sec.title}`, 2));
    sec.fields.forEach(([k, label]) => {
      sectionBlocks.push(field(label, state?.[k] || '', i === 0 ? sec.priority : null));
    });
  });

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'End-of-Life Checklist',
    contextTag: 'For my family',
    children: [
      ...buildAmanahCover(docx, {
        kicker: 'Amanah Life Planning Kit · 04',
        title: 'End-of-Life Checklist',
        subtitle: 'The one document your family will actually reach for.',
      }),

      themedQuote(docx, {
        arabic: 'مَا حَقُّ امْرِئٍ مُسْلِمٍ لَهُ شَيْءٌ يُوصِي فِيهِ يَبِيتُ لَيْلَتَيْنِ إِلَّا وَوَصِيَّتُهُ مَكْتُوبَةٌ عِنْدَهُ',
        translation: "It is not permissible for any Muslim who has something to bequeath to stay for two nights without having his last will and testament written and kept ready with him.",
        source: 'Sahih al-Bukhari 2738 · Sahih Muslim 1627',
      }),

      themedCallout(docx, {
        kicker: 'Why this matters',
        body: 'A prepared Muslim dies without leaving chaos. Your family will be grieving — they should not also have to guess your kafan preference, your executor\u2019s number, or where the will is stored. Fifteen minutes today saves fifteen days of stress tomorrow.',
        tone: 'emerald',
      }),

      themedCallout(docx, {
        kicker: 'Priority legend',
        body: '[MUST] — cannot proceed without you making a choice. Fill first.   [SHOULD] — strongly advised; assumes default otherwise.   [NICE] — personal touch that comforts your family.',
        tone: 'gold',
      }),

      ...sectionBlocks,

      themedOrnament(docx),
      themedBody(docx, isBlank ? 'Blank template — fill by hand or in Word, share with executor + family.' : `Personal record — generated ${stamp()}.`, { run: { italics: true, color: '9E9E9E', size: 16 } }),

      ...themedSignatureBlock(docx, {
        parties: [
          { label: 'Signature' },
          { label: 'Date' },
        ],
      }),
    ],
  })] });
  return docxToBlobOrDownload(Packer, doc, `amanah-eol-checklist-${isBlank ? 'blank' : 'mine'}-${stamp()}.docx`, asBlob);
};

// -----------------------------------------------------------------
// 5. ESTATE READINESS — Word checklist
// -----------------------------------------------------------------

const ESTATE_ITEMS = [
  ['Zakat calculated this year',                'Run the Zakat calculator on the lunar anniversary of your nisab date.'],
  ['Inheritance shares modelled',               'Walk the Faraid wizard with your current family.'],
  ['Will (wasiyyah) drafted, signed, witnessed', 'Use the Will tool, sign before two adult Muslim witnesses, store with executor.'],
  ['Waqf plan documented',                      'Optional but high-reward. Use the Waqf Planner.'],
  ['End-of-Life checklist filled & shared',     '15-minute form covering ghusl, burial, debts, contacts.'],
  ['Monthly budget tracked',                    'Use the Family Budget tool to balance income, expenses, savings pots.'],
  ['Sadaqah tracked',                           'Log every act of charity. Useful for reflection.'],
];

const LIFE_EVENTS = [
  'Marriage (new mahr, new heir share)',
  'Birth of a child (new heir, may reduce/eliminate residuary heirs)',
  'Move to a new country (new forced-heirship rules / probate law)',
  'Large purchase or sale (asset composition changes)',
  'New business or income source (adds Zakat basis)',
  'Divorce or death of a spouse (heir list changes)',
];

export const downloadEstateDocx = async (statusMap = {}, { asBlob = false } = {}) => {
  const docx = await loadDocx();
  const { Document, Packer } = docx;

  const statusText = (s) => s === 'done' ? '☑ Done'
                        : s === 'todo' ? '☐ To do'
                        : s === 'optional' ? '○ Optional' : '☐';

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'Islamic Estate Readiness',
    contextTag: 'Checklist',
    children: [
      ...buildAmanahCover(docx, {
        kicker: 'Amanah Life Planning Kit · 05',
        title: 'Islamic Estate Readiness',
        subtitle: 'Seven checkpoints for a Muslim who has planned well.',
      }),

      themedQuote(docx, {
        arabic: "وَلَنَبْلُوَنَّكُم بِشَيْءٍ مِّنَ الْخَوْفِ وَالْجُوعِ وَنَقْصٍ مِّنَ الْأَمْوَالِ وَالْأَنفُسِ وَالثَّمَرَاتِ ۗ وَبَشِّرِ الصَّابِرِينَ",
        translation: "And We will surely test you with something of fear, hunger, loss of wealth, lives and fruits. But give good tidings to the patient.",
        source: "Qur'an 2:155",
      }),

      themedCallout(docx, {
        kicker: 'Why this matters',
        body: 'Estate readiness is what turns intention into infrastructure. Each of the seven items below is a lever — miss one and the whole system stresses at the moment of grief. Refresh on every life event and your family inherits both wealth and peace.',
        tone: 'emerald',
      }),

      themedHeading(docx, 'The seven-point checklist'),
      themedTable(docx, {
        columns: [
          { label: '#', width: 6 },
          { label: 'Item', width: 42 },
          { label: 'Status', width: 15 },
          { label: 'How', width: 37 },
        ],
        rows: ESTATE_ITEMS.map(([label, note], i) => [
          String(i + 1),
          label,
          statusText(statusMap[label] || 'todo'),
          note,
        ]),
        tone: 'emerald',
      }),

      themedHeading(docx, 'Life events that trigger a review'),
      themedCallout(docx, {
        kicker: 'Refresh this checklist when…',
        body: LIFE_EVENTS.map((e, i) => `${i + 1}. ${e}`).join('   '),
        tone: 'gold',
      }),

      themedHeading(docx, 'Companion resources on Amanah'),
      themedBullet(docx, '/  — Zakat calculator (12 asset types, 83 country nisab).'),
      themedBullet(docx, '/inheritance  — Faraid wizard with 4-madhhab support.'),
      themedBullet(docx, '/will  — Sharia-compliant Wasiyyah writer with confidential annex.'),
      themedBullet(docx, '/life/waqf-plan  — Perpetual endowment (sadaqah jariyah) draft.'),
      themedBullet(docx, '/life/eol  — End-of-life checklist for your family.'),
      themedBullet(docx, '/succession  — One-click bundle: will + faraid + EOL + waqf in a single kit.'),

      themedOrnament(docx),
      ...themedSignatureBlock(docx, {
        parties: [
          { label: 'Owner signature' },
          { label: 'Date' },
          { label: 'Next review date' },
        ],
      }),
      themedBody(docx, `Snapshot generated ${stamp()}.`, { run: { italics: true, color: '9E9E9E', size: 16 } }),
    ],
  })] });
  return docxToBlobOrDownload(Packer, doc, `amanah-estate-readiness-${Object.keys(statusMap).length ? 'mine' : 'blank'}-${stamp()}.docx`, asBlob);
};
